import type { Theme } from '@src/app/themes';
import { IconButton } from '@src/shared/ui/Button';
import {
  CircleCheckIcon,
  CircleCloseIcon,
  CloseIcon,
  SquareInfoIcon,
} from '@src/shared/ui/icons';
import { Box, Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import type { ToastSeverity } from './types';

const icons = {
  success: CircleCheckIcon,
  warning: SquareInfoIcon,
  error: CircleCloseIcon,
};
const getTostColor = (theme: Theme): Record<string, string> => ({
  success: theme.palette.green[600],
  warning: theme.palette.orange[600],
  error: theme.palette.red[600],
});

const StyleSheet = {
  root:
    (severity: ToastSeverity, hasDescription: boolean) => (theme: Theme) => ({
      bgcolor: getTostColor(theme)[severity],
      color: 'common.white',
      borderRadius: 1,
      paddingX: '16px',
      paddingY: hasDescription ? '10px' : '12px',
    }),
  icon: (theme: Theme) => ({
    fontSize: 20,
    flexShrink: 0,
    color: theme.palette.common.white,
  }),
  textBlock: {
    flex: 1,
  },
  title: (theme: Theme) => ({
    color: theme.palette.common.white,
    fontWeight: 500,
  }),
  description: {
    lineHeight: 1,
  },
  closeButton: (theme: Theme) => ({
    color: 'common.white',
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
      color: theme.palette.common.white,
      width: '10px',
      height: '10px',
    },
  }),
};

type ToastProps = {
  title: string;
  description?: string;
  severity: ToastSeverity;
  onDismiss: () => void;
};

export default function Toast({
  title,
  description,
  severity,
  onDismiss,
}: ToastProps) {
  const Icon = icons[severity];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={StyleSheet.root(severity, !!description)}
    >
      <Icon sx={StyleSheet.icon} />
      <Box sx={StyleSheet.textBlock}>
        <Typography variant="body3" sx={StyleSheet.title}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body3" component="p" sx={StyleSheet.description}>
            {description}
          </Typography>
        )}
      </Box>
      <IconButton onClick={onDismiss} size="small" sx={StyleSheet.closeButton}>
        <CloseIcon />
      </IconButton>
    </Stack>
  );
}
